import {
	BranchesScreen,
	BranchesSkeleton,
} from "@commerce/branches/BranchesScreen";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/branches",
)({
	validateSearch: z.object({
		/** The branch open beside the map. */
		id: z.string().optional(),
	}),
	staticData: { fullBleed: true },
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	// The selected branch is client state over one list; changing it never refetches.
	shouldReload: false,
	loader: async ({ context }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		// A failed prefetch is not the page failing: the screen's own query retries it.
		await Promise.all([
			queryClient
				.ensureQueryData(
					orpc.stores.list.queryOptions({
						input: { organizationId },
					}),
				)
				.catch(() => null),
			context.isOrganizationAdmin
				? queryClient
						.ensureQueryData(
							orpc.orders.settings.get.queryOptions({
								input: { organizationId },
							}),
						)
						.catch(() => null)
				: null,
		]);
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	// Shown inside the Shell while the loader runs on a client navigation.
	pendingComponent: BranchesSkeleton,
	head: () => ({ meta: [{ title: `Branches - ${config.appName}` }] }),
	component: BranchesPage,
});

function BranchesPage() {
	const { organizationId, dehydratedState } = Route.useLoaderData();
	const { isOrganizationAdmin } = Route.useRouteContext();
	return (
		<AsyncBoundary
			fallback={<BranchesSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<BranchesScreen
				organizationId={organizationId}
				isAdmin={isOrganizationAdmin}
			/>
		</AsyncBoundary>
	);
}
